/**
 * GlobalConfigActionCard — Director asks the USER to change a Global Config
 * setting it may not change itself (e.g. cost auto-confirm).
 *
 * Emitted by the Director's `request_global_config_change(reason, settings_hint?)`
 * tool as a UserQuestion with `metadata.render_as === 'global_config_action_card'`.
 * Shows the Director's reason plus the LIVE values of the hinted settings.
 *
 * - "Open global config" and a click on the card body open the modal; they
 *   do NOT answer the question.
 * - "I'm finished" answers `finished`; the Director then re-reads the config
 *   diff on its next turn.
 *
 * Feature tag: `project-setup-card`.
 */
import { memo, useCallback, useEffect, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { createNadDebug } from '../../utils/debugLog'
import { openProjectSettings } from '../canvas-v2/projectSettingsEvents'
import type { AutoConfirmCostSettings } from './autoConfirmCost'
import { useOpenSettingsHandlers, useProjectConfigSummary } from './ProjectSetupCard'

const log = createNadDebug('dw:project-setup-card:action-card')

/** Settings the card knows how to display — mirrors the backend hint allowlist
 *  (`GLOBAL_CONFIG_SETTINGS_HINTS`). */
export type GlobalConfigHintField =
  | 'aspect_ratio'
  | 'style_id'
  | 'preferred_video_models'
  | 'preferred_image_model'
  | 'video_resolution'
  | 'auto_confirm_image'
  | 'auto_confirm_video'

const HINT_FIELD_SET: ReadonlySet<string> = new Set<GlobalConfigHintField>([
  'aspect_ratio',
  'style_id',
  'preferred_video_models',
  'preferred_image_model',
  'video_resolution',
  'auto_confirm_image',
  'auto_confirm_video',
])

function isHintField(value: string): value is GlobalConfigHintField {
  return HINT_FIELD_SET.has(value)
}

export interface GlobalConfigActionCardProps {
  projectId: string
  canvasId: string
  reason: string
  settingsHint: string[]
  autoConfirmCostSettings: AutoConfirmCostSettings
  onFinished: () => void
  disabled?: boolean
}

export const GlobalConfigActionCard = memo(function GlobalConfigActionCard({
  projectId,
  canvasId,
  reason,
  settingsHint,
  autoConfirmCostSettings,
  onFinished,
  disabled = false,
}: GlobalConfigActionCardProps) {
  const { t } = useTranslation('chat')
  const summary = useProjectConfigSummary(projectId, canvasId)
  const bodyHandlers = useOpenSettingsHandlers('global_config_action_card_body')
  const fields = settingsHint.filter(isHintField)
  const loading = t('projectSetupCard.loading')
  const onOff = (enabled: boolean) =>
    enabled ? t('globalConfigActionCard.on') : t('globalConfigActionCard.off')

  useEffect(() => {
    log({
      event: 'render',
      project_id: projectId,
      canvas_id: canvasId,
      settings_hint: fields.join(','),
      dropped_hints: settingsHint.length - fields.length,
    })
    // fields is derived from settingsHint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, canvasId, settingsHint])

  const valueFor = (field: GlobalConfigHintField): string => {
    switch (field) {
      case 'aspect_ratio':
        return summary.aspectRatio ?? loading
      case 'style_id':
        return summary.styleLabel ?? loading
      case 'preferred_video_models':
        return summary.videoModels ?? loading
      case 'preferred_image_model':
        return summary.imageModel ?? loading
      case 'video_resolution':
        return summary.config?.video_resolution ?? loading
      case 'auto_confirm_image':
        return onOff(autoConfirmCostSettings.image)
      case 'auto_confirm_video':
        return onOff(autoConfirmCostSettings.video)
    }
  }

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    log({ event: 'open_config', project_id: projectId, canvas_id: canvasId })
    openProjectSettings('global_config_action_card')
  }, [canvasId, projectId])

  const handleFinished = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    log({ event: 'finished', project_id: projectId, canvas_id: canvasId })
    onFinished()
  }, [canvasId, onFinished, projectId])

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={t('globalConfigActionCard.openGlobalConfig')}
      {...bodyHandlers}
      className="cursor-pointer rounded-lg border border-border bg-surface-1 p-4 transition-colors hover:border-border-strong hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-border"
      data-testid="global-config-action-card"
    >
      <h3 className="mb-1 text-sm font-medium text-text-primary">{t('globalConfigActionCard.title')}</h3>
      {reason && (
        <p className="mb-2 whitespace-pre-wrap text-xs font-medium text-text-secondary" data-testid="global-config-action-reason">
          {reason}
        </p>
      )}
      {fields.length > 0 && (
        <dl className="mb-2 divide-y divide-border-subtle">
          {fields.map((field) => (
            <div
              key={field}
              className="flex items-baseline justify-between gap-3 py-1"
              data-testid={`global-config-action-row-${field}`}
            >
              <dt className="shrink-0 text-xxs uppercase tracking-wider text-text-muted">
                {t(`globalConfigActionCard.fields.${field}`)}
              </dt>
              <dd className="min-w-0 truncate text-right text-xs font-medium text-text-primary">{valueFor(field)}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className="mb-3 text-xxs text-text-muted">{t('globalConfigActionCard.hint')}</p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleOpen}
          data-testid="global-config-action-open"
          className="rounded-md bg-chrome px-3 py-1.5 text-xs font-medium text-chrome-foreground transition-colors hover:bg-chrome/90 focus-visible:ring-2 focus-visible:ring-chrome/50"
        >
          {t('globalConfigActionCard.openGlobalConfig')}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={handleFinished}
          data-testid="global-config-action-finished"
          className="rounded-md px-3 py-1.5 text-xs font-medium text-text-muted transition-colors hover:bg-surface-3 hover:text-text-secondary focus-visible:ring-2 focus-visible:ring-border disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('globalConfigActionCard.finished')}
        </button>
      </div>
    </div>
  )
})
